import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';

const ChallengeCreation = ({ gymId, userId, onRefresh }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    challengeType: 'individual',
    goalType: 'workout_count',
    goalValue: '',
    pointsReward: '',
    prizeDescription: '',
    maxParticipants: '',
    startDate: '',
    endDate: '',
    rules: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const challengeTypes = [
    { value: 'individual', label: 'Individual Competition' },
    { value: 'team', label: 'Team-Based Event' },
    { value: 'gym_wide', label: 'Gym-Wide Initiative' }
  ];

  const goalTypes = [
    { value: 'workout_count', label: 'Workout Count' },
    { value: 'attendance_days', label: 'Attendance Days' },
    { value: 'class_count', label: 'Class Count' },
    { value: 'weight_loss', label: 'Weight Loss (lbs)' },
    { value: 'distance_miles', label: 'Distance (miles)' },
    { value: 'calories_burned', label: 'Calories Burned' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitting(true);

    try {
      const { error } = await supabase
        ?.from('challenges')
        ?.insert({
          gym_id: gymId,
          created_by: userId,
          title: formData?.title,
          description: formData?.description,
          challenge_type: formData?.challengeType,
          goal_type: formData?.goalType,
          goal_value: parseInt(formData?.goalValue),
          points_reward: parseInt(formData?.pointsReward),
          prize_description: formData?.prizeDescription || null,
          max_participants: formData?.maxParticipants ? parseInt(formData?.maxParticipants) : null,
          start_date: formData?.startDate,
          end_date: formData?.endDate,
          rules: formData?.rules || null,
          is_member_created: true,
          requires_approval: true,
          challenge_status: 'draft'
        });

      if (error) throw error;

      alert('Challenge created successfully! It will be active once approved by management.');
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        challengeType: 'individual',
        goalType: 'workout_count',
        goalValue: '',
        pointsReward: '',
        prizeDescription: '',
        maxParticipants: '',
        startDate: '',
        endDate: '',
        rules: ''
      });

      onRefresh?.();
    } catch (error) {
      console.error('Error creating challenge:', error);
      alert('Failed to create challenge. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Create New Challenge</h3>
        <p className="text-sm text-gray-600 mt-1">
          Design a custom fitness challenge for your gym community
        </p>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900">Basic Information</h4>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Challenge Title *
            </label>
            <Input
              name="title"
              value={formData?.title}
              onChange={handleInputChange}
              placeholder="e.g., 30-Day Fitness Challenge"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description *
            </label>
            <textarea
              name="description"
              value={formData?.description}
              onChange={handleInputChange}
              placeholder="Describe the challenge and what participants need to do"
              rows={4}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Challenge Type *
              </label>
              <Select
                name="challengeType"
                value={formData?.challengeType}
                onChange={handleInputChange}
                required
              >
                {challengeTypes?.map(type => (
                  <option key={type?.value} value={type?.value}>
                    {type?.label}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Goal Type *
              </label>
              <Select
                name="goalType"
                value={formData?.goalType}
                onChange={handleInputChange}
                required
              >
                {goalTypes?.map(type => (
                  <option key={type?.value} value={type?.value}>
                    {type?.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </div>

        {/* Challenge Goals */}
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900">Challenge Goals & Rewards</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Goal Value *
              </label>
              <Input
                type="number"
                name="goalValue"
                value={formData?.goalValue}
                onChange={handleInputChange}
                placeholder="e.g., 30"
                min="1"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Points Reward *
              </label>
              <Input
                type="number"
                name="pointsReward"
                value={formData?.pointsReward}
                onChange={handleInputChange}
                placeholder="e.g., 500"
                min="0"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Participants
              </label>
              <Input
                type="number"
                name="maxParticipants"
                value={formData?.maxParticipants}
                onChange={handleInputChange}
                placeholder="Leave empty for unlimited"
                min="1"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Prize Description
            </label>
            <Input
              name="prizeDescription"
              value={formData?.prizeDescription}
              onChange={handleInputChange}
              placeholder="e.g., Free month membership, $100 gift card"
            />
          </div>
        </div>

        {/* Duration */}
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900">Challenge Duration</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date *
              </label>
              <Input
                type="date"
                name="startDate"
                value={formData?.startDate}
                onChange={handleInputChange}
                min={new Date()?.toISOString()?.split('T')?.[0]}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date *
              </label>
              <Input
                type="date"
                name="endDate"
                value={formData?.endDate}
                onChange={handleInputChange}
                min={formData?.startDate || new Date()?.toISOString()?.split('T')?.[0]}
                required
              />
            </div>
          </div>
        </div>

        {/* Rules */}
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900">Challenge Rules</h4>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rules & Guidelines
            </label>
            <textarea
              name="rules"
              value={formData?.rules}
              onChange={handleInputChange}
              placeholder="List the rules and requirements for this challenge"
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            <Icon name="Info" className="w-4 h-4 inline mr-1" />
            Your challenge will require approval before going live
          </p>
          <Button type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Icon name="Loader" className="w-4 h-4 animate-spin mr-2" />
                Creating...
              </>
            ) : (
              <>
                <Icon name="PlusCircle" className="w-4 h-4 mr-2" />
                Create Challenge
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ChallengeCreation;